import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Alert,
  Badge,
  Box,
  Button,
  Center,
  Container,
  Group,
  Loader,
  Paper,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { CheckCircle, XCircle } from 'lucide-react';
import { milestoneApi } from '@/features/projects/data/milestone-api';

export const Route = createFileRoute('/client-milestone/$token')({
  component: ClientMilestonePage,
});

function ClientMilestonePage() {
  const { token } = Route.useParams();
  const [showReject, setShowReject] = useState(false);
  const [comment, setComment] = useState('');
  const [approverName, setApproverName] = useState('');
  const [done, setDone] = useState<'approved' | 'rejected' | null>(null);

  const { data: milestone, isLoading, error } = useQuery({
    queryKey: ['client-milestone', token],
    queryFn: () => milestoneApi.getByToken(token),
  });

  const approve = useMutation({
    mutationFn: () => milestoneApi.approveByToken(token, approverName.trim()),
    onSuccess: () => setDone('approved'),
  });

  const reject = useMutation({
    mutationFn: () => milestoneApi.rejectByToken(token, comment),
    onSuccess: () => setDone('rejected'),
  });

  if (isLoading) {
    return (
      <Center h='100vh'>
        <Loader />
      </Center>
    );
  }

  if (error || !milestone) {
    return (
      <Center h='100vh'>
        <Alert color='red' title='Lien invalide'>
          Ce lien de validation est invalide ou expiré.
        </Alert>
      </Center>
    );
  }

  if (done === 'approved') {
    return (
      <Center h='100vh'>
        <Stack align='center' gap='md'>
          <CheckCircle size={48} color='var(--mantine-color-green-6)' />
          <Title order={3}>Surface validée</Title>
          <Text c='dimmed'>Merci pour votre validation, {approverName.trim()}. Les travaux de protection peuvent maintenant commencer.</Text>
        </Stack>
      </Center>
    );
  }

  if (done === 'rejected') {
    return (
      <Center h='100vh'>
        <Stack align='center' gap='md'>
          <XCircle size={48} color='var(--mantine-color-orange-6)' />
          <Title order={3}>Demande de modification envoyée</Title>
          <Text c='dimmed'>Votre retour a été transmis à l'équipe. Nous reviendrons vers vous prochainement.</Text>
        </Stack>
      </Center>
    );
  }

  const isAwaitingClient = milestone.status === 'AwaitingClient';
  const statusColors: Record<string, string> = {
    Pending: 'gray',
    AwaitingClient: 'yellow',
    Approved: 'green',
    Completed: 'blue',
    Rejected: 'orange',
  };

  return (
    <Box bg='gray.0' mih='100vh' py='xl'>
      <Container size='sm'>
        <Stack gap='xl'>
          <Stack gap={4}>
            <Text size='sm' c='dimmed' tt='uppercase' fw={600} style={{ letterSpacing: '0.08em' }}>
              Mas Maya
            </Text>
            <Title order={2}>Validation de surface</Title>
          </Stack>

          <Paper withBorder p='xl' radius='lg' shadow='xs'>
            <Stack gap='lg'>
              <Group justify='space-between'>
                <Stack gap={4}>
                  <Text fw={600}>{milestone.sectionLabel ?? 'Surface'}</Text>
                  <Text size='sm' c='dimmed'>{milestone.descriptionKey ?? 'Votre surface est prête avant protection finale.'}</Text>
                </Stack>
                <Badge color={statusColors[milestone.status] ?? 'gray'} variant='light'>
                  {milestone.status === 'AwaitingClient' ? 'En attente de votre validation' : milestone.status}
                </Badge>
              </Group>

              {isAwaitingClient && !showReject && (
                <Stack gap='sm'>
                  <Text size='sm'>
                    Merci de valider l'aspect de la surface avant l'application du système de protection. Une fois validée, les travaux de protection pourront commencer.
                  </Text>
                  <TextInput
                    label='Votre nom et prénom'
                    description='Requis pour attester de votre validation'
                    placeholder='Jean Dupont'
                    required
                    value={approverName}
                    onChange={(e) => setApproverName(e.currentTarget.value)}
                  />
                  <Group>
                    <Button
                      color='green'
                      size='md'
                      loading={approve.isPending}
                      disabled={!approverName.trim()}
                      leftSection={<CheckCircle size={16} />}
                      onClick={() => approve.mutate()}
                    >
                      Valider la surface
                    </Button>
                    <Button
                      variant='light'
                      color='orange'
                      size='md'
                      onClick={() => setShowReject(true)}
                    >
                      Demander une modification
                    </Button>
                  </Group>
                </Stack>
              )}

              {isAwaitingClient && showReject && (
                <Stack gap='sm'>
                  <Textarea
                    label='Votre commentaire'
                    placeholder='Décrivez la modification souhaitée...'
                    value={comment}
                    onChange={(e) => setComment(e.currentTarget.value)}
                    rows={4}
                    autosize
                  />
                  <Group>
                    <Button variant='default' onClick={() => setShowReject(false)}>
                      Retour
                    </Button>
                    <Button
                      color='orange'
                      loading={reject.isPending}
                      onClick={() => reject.mutate()}
                    >
                      Envoyer la demande
                    </Button>
                  </Group>
                </Stack>
              )}

              {milestone.status === 'Approved' && (
                <Alert color='green' icon={<CheckCircle size={16} />}>
                  Surface validée le {milestone.approvedAt ? new Date(milestone.approvedAt).toLocaleDateString('fr-FR') : ''}
                  {milestone.approvedByName ? ` par ${milestone.approvedByName}` : ''}
                </Alert>
              )}

              {milestone.status === 'Rejected' && milestone.clientComment && (
                <Alert color='orange'>
                  Modification demandée : {milestone.clientComment}
                </Alert>
              )}
            </Stack>
          </Paper>

          <Text size='xs' c='dimmed' ta='center'>
            Mas Maya — Finitions & Enduits Décoratifs
          </Text>
        </Stack>
      </Container>
    </Box>
  );
}
